import React, { useMemo, useState } from "react";
import {
  ChargePrice,
  ChargePricePayload,
  useCreateChargePriceMutation,
  useUpdateChargePriceMutation,
  useGetChargeTypesQuery,
  useGetUserCategoriesQuery,
  useGetMembershipBandsQuery,
} from "../api/billingApi";

export default function ChargePriceForm({
  initial,
  defaultYear,
  onDone,
}: {
  initial?: ChargePrice;
  defaultYear?: number;
  onDone?: () => void;
}) {
  const [create, { isLoading: creating }] = useCreateChargePriceMutation();
  const [update, { isLoading: updating }] = useUpdateChargePriceMutation();
  const { data: chargeTypes = [] } = useGetChargeTypesQuery();
  //const { data: categories = [] } = useGetUserCategoriesQuery();
  // const { data: categoriesRes } = useGetUserCategoriesQuery();
  // const categories = categoriesRes?.data ?? [];
  const { data: categories = [] } = useGetUserCategoriesQuery();
  //console.log(categories?.data, "this is the returned follow");
  const [categoryId, setCategoryId] = useState<number | "">(
    (initial?.user_category_id ?? "") as any
  );

  const { data: bands = [] } = useGetMembershipBandsQuery(
    categoryId ? { user_category_id: Number(categoryId) } : undefined
  );

  const [form, setForm] = useState<ChargePricePayload>({
    charge_type_id: initial?.charge_type_id ?? chargeTypes[0]?.id ?? 0,
    user_category_id: initial?.user_category_id ?? null,
    membership_band_id: initial?.membership_band_id ?? null,
    year: initial?.year ?? defaultYear ?? new Date().getFullYear(),
    amount: initial?.amount ?? 0,
    currency: initial?.currency ?? "NGN",
    is_active: initial?.is_active ?? true,
  });

  const busy = creating || updating;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (initial?.id) {
        await update({ id: initial.id, body: form }).unwrap();
      } else {
        await create(form).unwrap();
      }
      onDone?.();
    } catch (err) {
      console.error(err);
      alert("Failed to save price");
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-600">Charge Type</label>
          <select
            className="w-full border rounded-md px-3 py-2"
            value={form.charge_type_id}
            onChange={(e) =>
              setForm((s) => ({ ...s, charge_type_id: Number(e.target.value) }))
            }
          >
            {chargeTypes.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({x.type})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-gray-600">Category (optional)</label>
          <select
            className="w-full border rounded-md px-3 py-2"
            value={categoryId}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : "";
              setCategoryId(v as any);
              setForm((s) => ({
                ...s,
                user_category_id: v ? Number(v) : null,
                membership_band_id: null,
              }));
            }}
          >
            <option value="">All Categories (global)</option>
            {Array.isArray(categories?.data) &&
              categories?.data?.map((x: any) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-gray-600">Band (optional)</label>
          <select
            className="w-full border rounded-md px-3 py-2"
            value={form.membership_band_id ?? ""}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                membership_band_id: e.target.value
                  ? Number(e.target.value)
                  : null,
              }))
            }
            disabled={!categoryId}
          >
            <option value="">No band</option>
            {bands.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-gray-600">Year (annual charges)</label>
          <input
            type="number"
            className="w-full border rounded-md px-3 py-2"
            value={form.year ?? ""}
            onChange={(e) =>
              setForm((s) => ({ ...s, year: Number(e.target.value) }))
            }
          />
        </div>

        <div>
          <label className="text-xs text-gray-600">Amount (kobo)</label>
          <input
            type="number"
            className="w-full border rounded-md px-3 py-2"
            value={form.amount}
            onChange={(e) =>
              setForm((s) => ({ ...s, amount: Number(e.target.value) }))
            }
          />
          <div className="text-[11px] text-gray-500 mt-1">
            Example: ₦50,000 = 5000000
          </div>
        </div>

        <div className="flex items-center gap-2 mt-6">
          <input
            type="checkbox"
            checked={!!form.is_active}
            onChange={(e) =>
              setForm((s) => ({ ...s, is_active: e.target.checked }))
            }
          />
          <span className="text-sm text-gray-700">Active</span>
        </div>
      </div>

      <button
        className="px-4 py-2 rounded-md bg-black text-white disabled:opacity-50"
        disabled={busy}
      >
        {busy ? "Saving..." : "Save Price"}
      </button>
    </form>
  );
}
